import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  assertNever,
  formatDate,
  getControlCategoryName,
  getLocalUTCOffset,
  getPartnerEventName,
  parseDate,
  pluralize,
  secondsInDay,
  service,
  TimeFormats,
  useAuthConfig,
} from "@theprelude/core";
import { Button, notifyError, notifySuccess, Truncate } from "@theprelude/ds";
import { Notification } from "@theprelude/sdk";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Filter } from "@/lib/filter";
import { getFrequency } from "@/lib/frequency";
import {
  isDurationFilter,
  ObjectFilter,
  OdataOperator,
  parseFilter,
  parseOdataId,
} from "@/lib/odata";
import {
  AdditionalFilter,
  flipOperator,
  getDurationLastSeenSeconds,
  isDurationId,
} from "../filter-form/additional-filters";
import { DEFAULT_PARSE } from "../filter-form/filter-form";
import { ResponsiveContainer } from "../responsive-container";
import { Column } from "../table/columns";
import { useTable } from "../table/hooks";
import { None, Unavailable } from "../table/unavaliable";
import { VirtualTable } from "../table/virtual-table";
import { DeleteNotificationModal } from "./delete-notification-modal";
import { allNotificationFilters, formatHour } from "./notification-form";

interface NotificationRow {
  id: string;
  frequency: string;
  conditions: string | undefined;
  channel: string;
}

const OPERATORS: Record<OdataOperator, string> = {
  "greater than": ">",
  "less than": "<",
  "is not": "≠",
  is: "=",
  contains: "contains",
  matches: "matches",
  "does not match": "does not match",
};

const notificationFrequency: Column<
  NotificationRow["frequency"],
  NotificationRow
> = {
  id: "frequency",
  enableSorting: true,
  cell: (info) => {
    const value = info.getValue();
    if (value === undefined) {
      return <Unavailable />;
    }
    return <Truncate text={value}>{value}</Truncate>;
  },
  meta: {
    label: "Frequency",
    widths: ["200px", "1fr"],
  },
};

const notificationConditions: Column<
  NotificationRow["conditions"],
  NotificationRow
> = {
  id: "conditions",
  enableSorting: true,
  cell: (info) => {
    const value = info.getValue();
    if (!value) {
      return <None />;
    }
    return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
  },
  meta: {
    label: "Conditions",
    widths: ["300px", "4fr"],
  },
};

const notificationChannel: Column<NotificationRow["channel"], NotificationRow> =
  {
    id: "channel",
    enableSorting: true,
    cell: (info) => <span>{info.getValue()}</span>,
    meta: {
      label: "Channel",
      widths: ["100px", "1fr"],
    },
  };

const useColumns = (
  setDeletingId: Dispatch<SetStateAction<string | undefined>>,
) => {
  const columnHelper = createColumnHelper<NotificationRow>();
  return [
    columnHelper.accessor("frequency", notificationFrequency),
    columnHelper.accessor("conditions", notificationConditions),
    columnHelper.accessor("channel", notificationChannel),
    columnHelper.display({
      id: "actions",
      enableHiding: false,
      enableSorting: false,
      cell: (info) => (
        <div className="flex w-full items-center justify-end gap-2">
          <Link
            to="/account/notifications/edit/$id"
            params={{ id: info.row.original.id }}
          >
            <Button type="button" intent="pill" className="whitespace-nowrap">
              Edit
            </Button>
          </Link>
          <Button
            type="button"
            intent="pill"
            onClick={() => setDeletingId(info.row.original.id)}
            className="whitespace-nowrap"
          >
            Delete
          </Button>
        </div>
      ),
      meta: {
        label: " ",
        widths: ["135px", "1fr"],
        canExport: false,
      },
    }),
  ];
};

function getDurationDaysText(filter: ObjectFilter) {
  const seconds = getDurationLastSeenSeconds(filter.value.value);
  const days = seconds / secondsInDay;
  return `${days} ${pluralize(days, "day")} ago`;
}

const getNotificationConditions = (
  notification: Notification,
): string | undefined => {
  if (!notification.filter) {
    return undefined;
  }

  const conditions = parseFilter(
    notification.filter,
    allNotificationFilters,
  ).map((nf) => {
    const filterDef = allNotificationFilters.find(
      (f) => parseOdataId(f.id) === parseOdataId(nf.id),
    );
    const parse = filterDef?.parse ?? DEFAULT_PARSE;
    const name = filterDef?.label ?? nf.id;
    const operator = isDurationId(nf.id)
      ? OPERATORS[flipOperator(parse(nf).operator || nf.operator)]
      : OPERATORS[parse(nf).operator || nf.operator];
    const value = getFilterValueText(filterDef, nf);

    return `${name} ${operator} ${value}`;
  });

  conditions.unshift(
    `Category = "${getControlCategoryName(notification.control_category)}"`,
    `State = "${getPartnerEventName(notification.event)}"`,
  );

  return conditions.join(" AND ");
};

const getFilterValueText = (
  filterDef: AdditionalFilter | undefined,
  filter: Filter,
) => {
  const { value } = filter;
  const type = filterDef?.type;

  if (typeof value === "object") {
    return isDurationFilter(filter) ? getDurationDaysText(filter) : value.value;
  }

  switch (type) {
    case "boolean": {
      return value ? "true" : "false";
    }
    case "date": {
      return formatDate(parseDate(String(value)), TimeFormats.DATE_SHORT);
    }
    case "number":
    case "duration": {
      return value;
    }
    case "text":
    case undefined: {
      return `"${value}"`;
    }
    default: {
      return assertNever(type);
    }
  }
};

export const NotificationsList = ({
  notifications,
}: {
  notifications: Notification[];
}) => {
  const authConfig = useAuthConfig();
  const [deletingId, setDeletingId] = useState<string>();

  const data = useMemo(
    () =>
      notifications.map(
        (notification): NotificationRow => ({
          id: notification.id,
          frequency: `${getFrequency(notification.run_code)} at ${formatHour(notification.scheduled_hour + getLocalUTCOffset())}`,
          channel: [
            notification.email && "Email",
            notification.slack && "Slack",
            notification.teams && "Teams",
          ]
            .filter(Boolean)
            .join(", "),
          conditions: getNotificationConditions(notification),
        }),
      ),
    [notifications],
  );

  const columns = useColumns(setDeletingId);

  const table = useTable({
    name: "add-policy-exception",
    data,
    columns,
  });

  const { mutate: deleteNotification, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => service(authConfig).scm.deleteNotification(id),
    onSuccess: () => {
      setDeletingId(undefined);
      notifySuccess({ message: "Notification has been deleted." });
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to delete this notification.",
        error,
      });
    },
  });

  const onDelete = () => {
    if (deletingId !== undefined) {
      deleteNotification(deletingId);
    }
  };

  return (
    <>
      <ResponsiveContainer className="flex-1 overflow-hidden">
        {({ height }) => (
          <VirtualTable
            table={table}
            height={height}
            emptyState={<EmptyState />}
          />
        )}
      </ResponsiveContainer>
      <DeleteNotificationModal
        open={deletingId !== undefined}
        isDeleting={isDeleting}
        onConfirm={onDelete}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingId(undefined);
          }
        }}
      />
    </>
  );
};

const EmptyState = () => <span>No notifications have been created.</span>;
